import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { api, streamUrl } from "@/lib/api";
import { RefreshCw, VideoOff } from "lucide-react";

const useServerStatus = (cam, active) => {
  const [status, setStatus] = useState("connecting");
  useEffect(() => {
    if (cam.stream_type !== "rtsp" || !active) return;
    const tick = () => api.get(`/cameras/${cam.id}/status`).then((r) => setStatus(r.data.status)).catch(() => {});
    const iv = setInterval(tick, 4000);
    const t = setTimeout(tick, 1500);
    return () => { clearInterval(iv); clearTimeout(t); };
  }, [cam.id, cam.stream_type, active]);
  return [status, setStatus];
};

const HlsVideo = ({ src, onState }) => {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    let hls;
    if (v.canPlayType("application/vnd.apple.mpegurl")) v.src = src;
    else if (Hls.isSupported()) {
      hls = new Hls({ liveDurationInfinity: true });
      hls.loadSource(src);
      hls.attachMedia(v);
      hls.on(Hls.Events.ERROR, (_, d) => d.fatal && onState("offline"));
    } else onState("offline");
    return () => hls && hls.destroy();
  }, [src, onState]);
  return (
    <video ref={ref} className="cam-media" autoPlay muted playsInline
      onPlaying={() => onState("live")} onError={() => onState("offline")} />
  );
};

export const CameraPlayer = ({ cam, onStatus }) => {
  const [key, setKey] = useState(Date.now());
  const [direct, setDirect] = useState("connecting");
  const [server, setServer] = useServerStatus(cam, true);
  const status = cam.stream_type === "rtsp" ? (server === "idle" ? "connecting" : server) : direct;

  useEffect(() => { onStatus && onStatus(status); }, [status, onStatus]);

  const retry = () => { setKey(Date.now()); setDirect("connecting"); setServer("connecting"); };

  return (
    <div className="cam-frame" data-testid={`camera-player-${cam.id}`}>
      {cam.stream_type === "rtsp" && (
        <img key={key} className="cam-media" src={streamUrl(cam.id, key)} alt={cam.name}
          onLoad={() => setServer("live")} />
      )}
      {cam.stream_type === "mjpeg" && (
        <img key={key} className="cam-media" src={cam.url} alt={cam.name}
          onLoad={() => setDirect("live")} onError={() => setDirect("offline")} />
      )}
      {cam.stream_type === "hls" && <HlsVideo key={key} src={cam.url} onState={setDirect} />}

      {status !== "live" && (
        <div className="cam-overlay" data-testid={`camera-overlay-${cam.id}`}>
          {status === "offline" ? <VideoOff size={28} /> : <span className="cam-spinner" />}
          <span className="mono text-xs tracking-[0.2em] uppercase">
            {status === "offline" ? "Sem sinal" : "Conectando..."}
          </span>
          <button className="btn-ghost" onClick={retry} data-testid={`camera-retry-${cam.id}`}>
            <RefreshCw size={14} /> Reconectar
          </button>
        </div>
      )}
    </div>
  );
};
